/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useRef, useState } from "react";
import { Check, ListFilter, Plus, X } from "lucide-react";
import { cn } from "../lib/utils";
import { DropdownPanel } from "./ui/DropdownPanel";
import { DateTimePicker } from "./ui/DateTimePicker";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import {
  filterValueLabel,
  toDateValue,
  toYmd,
  type ActiveFilter,
  type FilterField,
} from "../lib/filters";

/**
 * Linear-style filters. The toolbar trigger sits at the row's end at Button height; applied
 * filters wrap onto their own full-width row of chips with an add button and Clear filters.
 * One menu serves both triggers: pick a field, then a value (searchable list, or a date-only
 * picker). One chip per field; picking again replaces it.
 */
export function FilterBar({
  fields,
  active,
  onChange,
}: {
  fields: FilterField[];
  active: ActiveFilter[];
  onChange: (next: ActiveFilter[]) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pickingField, setPickingField] = useState<FilterField | null>(null);
  const [search, setSearch] = useState("");
  const [draftDate, setDraftDate] = useState<Date | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = anchor;

  const open = anchor !== null;

  const close = () => {
    setAnchor(null);
    setPickingField(null);
    setSearch("");
    setDraftDate(null);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (contentRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      close();
    } else {
      setAnchor(event.currentTarget);
    }
  };

  const currentValue = (field: FilterField) =>
    active.find((filter) => filter.key === field.key)?.value ?? null;

  const beginField = (field: FilterField) => {
    const existing = currentValue(field);
    setPickingField(field);
    setSearch("");
    setDraftDate(field.kind === "date" && existing !== null ? toDateValue(existing) : null);
  };

  const beginEdit = (event: React.MouseEvent<HTMLElement>, field: FilterField) => {
    setAnchor(event.currentTarget);
    beginField(field);
  };

  const apply = (field: FilterField, value: string) => {
    onChange([...active.filter((filter) => filter.key !== field.key), { key: field.key, value }]);
    close();
  };

  return (
    <>
      <Button
        variant="outline"
        size="md"
        aria-label="Filter"
        className="self-end sm:ml-auto sm:self-auto"
        onClick={toggle}
      >
        <ListFilter size={15} className="mr-1.5" />
        Filter
      </Button>
      {active.length > 0 && (
        <div className="flex w-full flex-wrap items-center gap-2">
          {active.map((filter) => {
            const field = fields.find((candidate) => candidate.key === filter.key);
            if (!field) {
              return null;
            }
            return (
              <span
                key={filter.key}
                className="glass inline-flex h-9 items-center gap-1.5 rounded-full py-1 pl-3 pr-1.5 text-xs text-text-secondary"
              >
                <button
                  type="button"
                  aria-label={`Edit ${field.label} filter`}
                  className="inline-flex items-center gap-1.5 rounded-full transition-colors hover:text-text-primary"
                  onClick={(event) => beginEdit(event, field)}
                >
                  <span className="text-text-tertiary">{field.label}</span>
                  <span>is</span>
                  <span className="font-medium text-text-primary">
                    {filterValueLabel(field, filter.value)}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${field.label} filter`}
                  className="grid size-5 place-items-center rounded-full text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary"
                  onClick={() => onChange(active.filter((other) => other.key !== filter.key))}
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-full border border-dashed border-border px-3 text-xs text-text-tertiary transition-colors hover:border-border-hover hover:text-text-primary"
            onClick={toggle}
          >
            <Plus size={13} />
            Add filter
          </button>
          <span className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => onChange([])}>
            Clear filters
          </Button>
        </div>
      )}
      <DropdownPanel
        triggerRef={anchorRef}
        isOpen={open}
        maxHeight={340}
        className="z-40 flex flex-col overflow-hidden"
      >
        <div ref={contentRef} className="flex min-h-0 w-64 flex-col p-2">
          {pickingField === null ? (
            <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
              {fields.map((field) => (
                <li key={field.key}>
                  <button
                    type="button"
                    className="w-full rounded-lg px-3 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
                    onClick={() => beginField(field)}
                  >
                    {field.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : pickingField.kind === "choice" ? (
            <>
              {pickingField.searchable !== false && (
                <Input
                  autoFocus
                  placeholder="Filter…"
                  aria-label={`Filter ${pickingField.label} values`}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
              <ul className="mt-2 min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                {(pickingField.options ?? [])
                  .filter((option) =>
                    option.label.toLowerCase().includes(search.trim().toLowerCase()),
                  )
                  .map((option) => (
                    <li key={option.value}>
                      <button
                        type="button"
                        aria-pressed={option.value === currentValue(pickingField)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-bg-hover hover:text-text-primary",
                          option.value === currentValue(pickingField)
                            ? "font-medium text-text-primary"
                            : "text-text-secondary",
                        )}
                        onClick={() => apply(pickingField, option.value)}
                      >
                        {option.label}
                        {option.value === currentValue(pickingField) && (
                          <Check size={14} className="shrink-0 text-interactive" />
                        )}
                      </button>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <div className="space-y-3 p-1">
              <DateTimePicker
                variant="date"
                editOnly
                datePlaceholder="MM/DD/YYYY"
                label={pickingField.label}
                dateValue={draftDate}
                onDateChange={setDraftDate}
              />
              <Button
                size="sm"
                disabled={draftDate === null}
                onClick={() => draftDate && apply(pickingField, toYmd(draftDate))}
              >
                Apply
              </Button>
            </div>
          )}
        </div>
      </DropdownPanel>
    </>
  );
}
